import { createContext, useContext, useState, ReactNode, Dispatch, SetStateAction } from "react";
import { MRT_PaginationState, MRT_SortingState } from "material-react-table";
import { useAllIcvs, Icv } from "src/api/client-api/icv/useAllIcvs";
import { useDelayedSearch } from "src/utils/useDelayedSearch";
import { useGridColumns, GridColumnsType } from "src/components/Grid/hooks/columnsExtension/useGridColumns.tsx";
import { useIcvColumns } from "./useIcvColumns.tsx";

type IcvListContextInitialValues = {
    icvPage: MRT_PaginationState;
    setIcvPage: Dispatch<SetStateAction<MRT_PaginationState>>;

    sorting: MRT_SortingState;
    setSorting: Dispatch<SetStateAction<MRT_SortingState>>;

    totalItems: number | undefined;

    icvs: Icv[] | undefined;
    mrt_columns: GridColumnsType<Icv>;
    isLoading: boolean;

    searchIcv: string;
    delayedSearchIcv: string;
    setSearchIcv: (arg1: string) => void;
    resetSearch: () => void;

    // Modal states
    showCreateModal: boolean;
    setShowCreateModal: Dispatch<SetStateAction<boolean>>;

    gridKey: string;
};

const IcvListContext = createContext<IcvListContextInitialValues | undefined>(undefined);

type IcvListContextProviderProps = {
    children: ReactNode;
};

export const IcvListContextProvider = (props: IcvListContextProviderProps) => {
    const { children } = props;

    const gridKey = "settings-icvs-grid";

    const [icvPage, setIcvPage] = useState<MRT_PaginationState>({
        pageIndex: 0,
        pageSize: 20,
    });
    const [sorting, setSorting] = useState<MRT_SortingState>([{ id: "name", desc: false }]);

    // Modal states
    const [showCreateModal, setShowCreateModal] = useState(false);

    const search = useDelayedSearch();
    const { icvs, isLoading, totalItems } = useAllIcvs({
        page: icvPage.pageIndex + 1,
        itemsPerPage: icvPage.pageSize,
        search: search.delayedSearch,
        sorting: sorting,
    });

    const mrt_icvColumns = useIcvColumns({ icvs });
    const mrt_columns = useGridColumns<Icv>(mrt_icvColumns, gridKey);

    const items: IcvListContextInitialValues = {
        mrt_columns,

        icvPage,
        setIcvPage,
        totalItems,

        sorting,
        setSorting,

        icvs,
        isLoading,

        searchIcv: search.search,
        delayedSearchIcv: search.delayedSearch,
        setSearchIcv: search.setSearch,
        resetSearch: search.resetSearch,

        // Modal states
        showCreateModal,
        setShowCreateModal,

        gridKey: gridKey,
    };

    return <IcvListContext.Provider value={items}>{children}</IcvListContext.Provider>;
};

export const useIcvListContext = () => {
    const context = useContext(IcvListContext);
    if (context === undefined) {
        throw new Error("useIcvListContext must be used within an IcvListContextProvider");
    }
    return context;
};
